/**
 * SHARED CORE - Reusable patterns across all write tools
 * 
 * This module provides common functionality for note_write, sheet_write,
 * canvas_write, and slide_write tools.
 */

import { StandardToolResult } from '../../../types/standardToolResults';
import { CommandExecutionContext } from '../../../types/commands';
import { getApplicationForEntity } from '../../../services/applicationRegistry';
import { generateGrokImage, searchAndDownloadUnsplash } from '../../../services/imageService';

/**
 * Base parameters shared by all write tools
 */
export interface BaseWriteParams {
  entityId: string;
  operation: 'insert' | 'replace' | 'update' | 'delete' | 'batch';
  target?: any;
  content?: any;
  attrs?: any;
  operations?: any[];
}

/**
 * Write operation result
 */
export interface WriteResult {
  affectedNodes: string[];
  stats: { nodesModified: number };
  messageContext?: {
    fullContent?: string;
    contentPreview?: string;
    operationSummary?: string;
  };
}

/**
 * Save behavior configuration (universal across all apps)
 */
export interface SaveBehavior {
  autoSaveEnabled: boolean;      // Default: true
  saveDebounceMs: number;         // Default: 500ms
  saveMaxWaitMs: number;          // Default: 5000ms
}

export const DEFAULT_SAVE_BEHAVIOR: SaveBehavior = {
  autoSaveEnabled: true,
  saveDebounceMs: 500,
  saveMaxWaitMs: 5000
};

/**
 * Standard anchor points for positioning
 * Short names map to edge centers for convenience
 */
export type AnchorPoint = 
  | 'top-left' | 'top-center' | 'top-right' | 'top'    // 'top' = alias for 'top-center'
  | 'left-center' | 'center' | 'right-center' | 'left' | 'right'  // 'left'/'right' = aliases for center
  | 'bottom-left' | 'bottom-center' | 'bottom-right' | 'bottom'   // 'bottom' = alias for 'bottom-center'
  | 'start' | 'end';

/**
 * Get bounds for entity type
 */
function getBoundsForEntity(app: any): { minX: number; maxX: number; minY: number; maxY: number } {
  // Determine entity type from app config or class name
  const config = app.getConfig?.();
  const appName = config?.appName || '';
  
  if (appName === 'canvas' || appName.includes('Canvas')) {
    // Canvas: center origin, -2500 to +2500
    return { minX: -2500, maxX: 2500, minY: -2500, maxY: 2500 };
  } else if (appName === 'presentations' || appName.includes('Presentation')) {
    // Presentations: top-left origin, 0-800 x 0-450
    return { minX: 0, maxX: 800, minY: 0, maxY: 450 };
  } else {
    // Default: no bounds checking
    return { minX: -Infinity, maxX: Infinity, minY: -Infinity, maxY: Infinity };
  }
}

/**
 * Calculate the absolute coordinates of an anchor point on an element
 */
function getAnchorPointCoordinates(
  node: any,
  anchorPoint: AnchorPoint
): { x: number; y: number } {
  const pos = node.attrs?.position;
  
  if (!pos) {
    throw new Error(`Node ${node.id} has no position attribute`);
  }
  
  const x = pos.x ?? 0;
  const y = pos.y ?? 0;
  const width = pos.width ?? 0;
  const height = pos.height ?? 0;
  
  // Warn about zero-size elements
  if (width === 0 || height === 0) {
    console.warn(`[entityWriteCore] Element ${node.id} has zero size (${width}x${height}), anchor points may not work as expected`);
  }
  
  switch (anchorPoint) {
    case 'top-left':
      return { x, y };
    case 'top-center':
    case 'top':  // Alias: 'top' = center of top edge
      return { x: x + width / 2, y };
    case 'top-right':
      return { x: x + width, y };
    case 'left-center':
    case 'left':  // Alias: 'left' = center of left edge
      return { x, y: y + height / 2 };
    case 'center':
      return { x: x + width / 2, y: y + height / 2 };
    case 'right-center':
    case 'right':  // Alias: 'right' = center of right edge
      return { x: x + width, y: y + height / 2 };
    case 'bottom-left':
      return { x, y: y + height };
    case 'bottom-center':
    case 'bottom':  // Alias: 'bottom' = center of bottom edge
      return { x: x + width / 2, y: y + height };
    case 'bottom-right':
      return { x: x + width, y: y + height };
    case 'start':
      // For lines/arrows: start point
      if (node.attrs?.semantic?.x1 !== undefined) {
        return { 
          x: x + node.attrs.semantic.x1, 
          y: y + node.attrs.semantic.y1 
        };
      }
      console.warn(`[entityWriteCore] Anchor point "start" used on non-line element ${node.id}, using top-left`);
      return { x, y };
    case 'end':
      // For lines/arrows: end point
      if (node.attrs?.semantic?.x2 !== undefined) {
        return { 
          x: x + node.attrs.semantic.x2, 
          y: y + node.attrs.semantic.y2 
        };
      }
      console.warn(`[entityWriteCore] Anchor point "end" used on non-line element ${node.id}, using bottom-right`);
      return { x: x + width, y: y + height };
    default:
      console.warn(`[entityWriteCore] Unknown anchor point: ${anchorPoint}, using top-left`);
      return { x, y };
  }
}

/**
 * Calculate the offset for a given anchor point on self
 */
function getSelfAnchorOffset(
  width: number,
  height: number,
  selfAnchor: AnchorPoint
): { x: number; y: number } {
  switch (selfAnchor) {
    case 'top-left':
      return { x: 0, y: 0 };
    case 'top-center':
      return { x: width / 2, y: 0 };
    case 'top-right':
      return { x: width, y: 0 };
    case 'left-center':
      return { x: 0, y: height / 2 };
    case 'center':
      return { x: width / 2, y: height / 2 };
    case 'right-center':
      return { x: width, y: height / 2 };
    case 'bottom-left':
      return { x: 0, y: height };
    case 'bottom-center':
      return { x: width / 2, y: height };
    case 'bottom-right':
      return { x: width, y: height };
    case 'start':
      return { x: 0, y: 0 };
    case 'end':
      return { x: width, y: height };
    default:
      return { x: 0, y: 0 };
  }
}

/**
 * Resolve anchored position to absolute coordinates
 */
async function resolveAnchoredPosition(
  content: any,
  app: any
): Promise<any> {
  // Check if content has anchored position
  const position = content?.attrs?.position;
  
  if (!position || !position.anchorTo) {
    return content; // No anchor, return as-is
  }
  
  console.log('[entityWriteCore] Resolving anchored position:', {
    anchorTo: position.anchorTo,
    anchorPoint: position.anchorPoint
  });
  
  // Ensure indices are built
  if (typeof app.buildCoreIndices === 'function') {
    await app.buildCoreIndices();
  }
  
  // Lookup anchor node
  const anchorNode = app.byIdIndex?.get(position.anchorTo);
  
  if (!anchorNode) {
    throw new Error(`Anchor node not found: ${position.anchorTo}. Use entity_get_metadata to find valid node IDs.`);
  }
  
  // Check for chained anchors (not allowed)
  if (anchorNode.attrs?.position?.anchorTo) {
    throw new Error(`Cannot anchor to an anchored element (${position.anchorTo}). Anchor must have absolute position.`);
  }
  
  // Get anchor point on target node
  const anchorPoint = (position.anchorPoint || 'center') as AnchorPoint;
  const anchorCoords = getAnchorPointCoordinates(anchorNode, anchorPoint);
  
  console.log('[entityWriteCore] Anchor coordinates:', anchorCoords);
  
  // Get self anchor offset
  const selfAnchor = (position.selfAnchor || 'top-left') as AnchorPoint;
  const selfOffset = getSelfAnchorOffset(
    position.width,
    position.height,
    selfAnchor
  );
  
  // Apply user offset
  const userOffset = position.offset || { x: 0, y: 0 };
  
  // Calculate final position
  const finalX = anchorCoords.x + userOffset.x - selfOffset.x;
  const finalY = anchorCoords.y + userOffset.y - selfOffset.y;
  
  console.log('[entityWriteCore] Resolved position:', { 
    x: finalX, 
    y: finalY,
    selfOffset,
    userOffset 
  });
  
  // Validate bounds
  const bounds = getBoundsForEntity(app);
  if (finalX < bounds.minX || finalX + position.width > bounds.maxX ||
      finalY < bounds.minY || finalY + position.height > bounds.maxY) {
    throw new Error(
      `Anchored element would be positioned outside valid bounds. ` +
      `Resolved position: (${finalX.toFixed(1)}, ${finalY.toFixed(1)}), ` +
      `Size: ${position.width}x${position.height}, ` +
      `Valid bounds: x(${bounds.minX} to ${bounds.maxX}), y(${bounds.minY} to ${bounds.maxY}). ` +
      `Try adjusting the offset or using a different anchor point.`
    );
  }
  
  // Return content with resolved absolute position
  return {
    ...content,
    attrs: {
      ...content.attrs,
      position: {
        x: finalX,
        y: finalY,
        width: position.width,
        height: position.height,
        z: position.z
      },
      semantic: {
        ...content.attrs?.semantic,
        // Store anchor metadata for UI/debugging
        anchoredTo: {
          nodeId: position.anchorTo,
          anchorPoint,
          selfAnchor,
          offset: userOffset
        }
      }
    }
  };
}

/**
 * Resolve anchored positions in content (handles single or array)
 * Also handles line/arrow start/end transformation
 */
async function resolveAnchors(
  params: BaseWriteParams,
  app: any
): Promise<BaseWriteParams> {
  const { content, operation, operations } = params;
  
  // Handle batch operations
  if (operation === 'batch' && operations) {
    const resolvedOperations = [];
    
    for (const op of operations) {
      if (op.content) {
        // First transform line/arrow start/end format
        const transformedContent = await transformLineArrowContent(op.content, app);
        // Then resolve regular anchored positions
        const resolvedContent = await resolveAnchoredPosition(transformedContent, app);
        resolvedOperations.push({
          ...op,
          content: resolvedContent
        });
      } else {
        resolvedOperations.push(op);
      }
    }
    
    return {
      ...params,
      operations: resolvedOperations
    };
  }
  
  // Handle single content
  if (content) {
    // Handle array of content
    if (Array.isArray(content)) {
      const resolvedContent = [];
      for (const item of content) {
        // First transform line/arrow start/end format
        const transformedItem = await transformLineArrowContent(item, app);
        // Then resolve regular anchored positions
        const resolved = await resolveAnchoredPosition(transformedItem, app);
        resolvedContent.push(resolved);
      }
      
      return {
        ...params,
        content: resolvedContent
      };
    }
    
    // Handle single content object
    // First transform line/arrow start/end format
    const transformedContent = await transformLineArrowContent(content, app);
    // Then resolve regular anchored positions
    const resolvedContent = await resolveAnchoredPosition(transformedContent, app);
    return {
      ...params,
      content: resolvedContent
    };
  }
  
  return params;
}

/**
 * Extract readable text content from content/attrs parameters for UI display
 */
export function extractContentFromParams(
  content: any, 
  attrs: any, 
  operation: string, 
  operations?: any[]
): string | null {
  // For delete operations, no content to extract
  if (operation === 'delete') {
    return null;
  }
  
  // Handle batch operations - extract from operations array
  if (operation === 'batch' && Array.isArray(operations) && operations.length > 0) {
    const contentPieces: string[] = [];
    for (const op of operations) {
      const opType = op.op || op.operation || 'unknown';
      const opContent = extractContentFromParams(op.content, op.attrs, opType);
      if (opContent) {
        contentPieces.push(opContent);
      }
    }
    if (contentPieces.length > 0) {
      // For many operations, just show count
      if (contentPieces.length > 3) {
        return `${contentPieces.length} operations`;
      }
      // For few operations, show preview
      return contentPieces.join(' | ');
    }
    return `${operations.length} operations`;
  }
  
  // Extract from structured content object (Notes, Canvas, Presentations, Sheets)
  if (content && typeof content === 'object' && !Array.isArray(content)) {
    // Priority 1: attrs.text (universal pattern for all text)
    if (content.attrs?.text) {
      return content.attrs.text;
    }
    
    // Priority 2: Sheet cell value
    if (content.className === 'table_cell') {
      const cellRef = content.attrs?.position?.cellRef || content.attrs?.semantic?.cellRef;
      const cellValue = content.attrs?.semantic?.value ?? content.attrs?.value;
      if (cellRef && cellValue !== undefined) {
        return `${cellRef}=${cellValue}`;
      }
    }
    
    // Priority 3: Legacy content field
    if (content.content && typeof content.content === 'string') {
      return content.content;
    }
    
    // For non-text elements (shapes, images), return a description
    if (content.className) {
      const className = content.className;
      if (className.includes('shape') || className.includes('image')) {
        return `[${className}]`;
      }
    }
  }
  
  // Extract from array of content objects
  if (Array.isArray(content)) {
    const texts = content
      .map((item: any) => {
        // Extract text from text elements
        if (item.attrs?.text) return item.attrs.text;
        if (item.content) return item.content;
        
        // Extract values from sheet cells
        if (item.className === 'table_cell') {
          const cellRef = item.attrs?.position?.cellRef || item.attrs?.semantic?.cellRef;
          const cellValue = item.attrs?.semantic?.value ?? item.attrs?.value;
          if (cellRef && cellValue !== undefined) {
            return `${cellRef}=${cellValue}`;
          }
        }
        
        return null;
      })
      .filter(Boolean);
    if (texts.length > 0) {
      return texts.join(' | ');
    }
  }
  
  // Extract from plain string (legacy)
  if (typeof content === 'string') {
    return content;
  }
  
  // Extract from attrs (Sheets operations)
  if (attrs && typeof attrs === 'object') {
    // For Sheets, show cell references
    const cellRefs = Object.keys(attrs);
    if (cellRefs.length > 0) {
      return `Updated ${cellRefs.length} cell${cellRefs.length > 1 ? 's' : ''}: ${cellRefs.slice(0, 5).join(', ')}${cellRefs.length > 5 ? '...' : ''}`;
    }
  }
  
  return null;
}

/**
 * Generate user-facing contextual message
 */
export function generateContextualMessage(
  operation: string, 
  entityType: string, 
  content: string | null
): string {
  // Generate message based on operation
  switch (operation) {
    case 'insert':
      if (content && content.length > 0) {
        return `Inserted content in ${entityType}`;
      }
      return `Inserted content in ${entityType}`;
      
    case 'update':
      return `Updated ${entityType}`;
      
    case 'replace':
      return `Replaced content in ${entityType}`;
      
    case 'delete':
      return `Deleted content from ${entityType}`;
      
    case 'batch':
      return `Applied batch operations to ${entityType}`;
      
    default:
      return `Modified ${entityType}`;
  }
}

/**
 * Validate entity ID matches expected prefix
 */
export function validateEntityId(entityId: string, allowedPrefix: string): string | null {
  if (!entityId) {
    return 'Entity ID is required';
  }
  if (!entityId.startsWith(allowedPrefix)) {
    return `Invalid entity ID. Expected ${allowedPrefix}* but got ${entityId}`;
  }
  return null;
}

export { formatWriteResult } from '../../../tools/formatWriteResult';

/**
 * Pre-fetch images for a single content item
 * Converts imageSource + prompt/query to actual image URL
 */
async function preFetchImagesInContent(item: any): Promise<void> {
  if (!item || typeof item !== 'object') return;
  
  // Check if this is an image element with imageSource
  const isImageElement = item.className === 'canvas-image' || 
                        item.className === 'slide-image' ||
                        item.className === 'note-image';
  
  if (isImageElement && item.attrs?.semantic?.imageSource) {
    const semantic = item.attrs.semantic;
    const imageSource = semantic.imageSource;
    
    console.log('[executeWrite] 🖼️ Image fetch required:', {
      className: item.className,
      source: imageSource,
      prompt: semantic.prompt,
      query: semantic.query
    });
    
    try {
      let imageUrl: string;
      
      if (imageSource === 'grok' && semantic.prompt) {
        // Generate image with Grok
        const result = await generateGrokImage(semantic.prompt);
        imageUrl = result.url;
      } else if (imageSource === 'unsplash' && semantic.query) {
        // Fetch from Unsplash
        const result = await searchAndDownloadUnsplash(semantic.query);
        imageUrl = result.url;
      } else {
        throw new Error(`Invalid imageSource configuration: ${imageSource} (missing ${imageSource === 'grok' ? 'prompt' : 'query'})`);
      }
      
      // ✅ CRITICAL FIX: Validate URL before storing
      if (!imageUrl || typeof imageUrl !== 'string' || imageUrl.trim() === '') {
        throw new Error(`Image service returned invalid URL: ${imageUrl}`);
      }
      
      // Basic URL format validation
      if (!imageUrl.startsWith('http://') && !imageUrl.startsWith('https://') && !imageUrl.startsWith('data:')) {
        throw new Error(`Image URL must start with http://, https://, or data: - got: ${imageUrl.substring(0, 50)}`);
      }
      
      console.log('[executeWrite] ✅ Image fetched:', imageUrl.substring(0, 60) + '...');
      
      // Replace imageSource with actual src
      item.attrs.semantic = {
        ...semantic,
        src: imageUrl
      };
      
      // Remove temporary fields (defensive check)
      if (item.attrs?.semantic) {
        delete item.attrs.semantic.imageSource;
        delete item.attrs.semantic.prompt;
        delete item.attrs.semantic.query;
      }
      
    } catch (error: any) {
      console.error('[executeWrite] ❌ Image fetch failed:', error.message);
      throw new Error(`Failed to fetch image for ${item.className}: ${error.message}`);
    }
  }
}

/**
 * Transform line/arrow start/end format to position+semantic format
 * NEW FORMAT: { start: {x,y} | {anchorTo}, end: {x,y} | {anchorTo} }
 * INTERNAL FORMAT: { position: {x,y,w,h}, semantic: {x1,y1,x2,y2} }
 */
async function transformLineArrowContent(
  content: any,
  app: any
): Promise<any> {
  // Check if this is a line/arrow with start/end format
  const isCanvasLineOrArrow = content.className === 'shape-line' || content.className === 'shape-arrow';
  const isSlideShapeLineOrArrow = 
    content.className === 'slide-shape' && 
    (content.attrs?.semantic?.shapeType === 'line' || content.attrs?.semantic?.shapeType === 'arrow');
  
  const isLineOrArrow = isCanvasLineOrArrow || isSlideShapeLineOrArrow;
  const hasStartEnd = content.attrs?.start && content.attrs?.end;
  
  if (!isLineOrArrow || !hasStartEnd) {
    return content; // Not a line/arrow or already in position+semantic format
  }
  
  if (!app) {
    throw new Error('Application instance required to transform line/arrow content');
  }
  
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  console.log('[entityWriteCore] 🔄 TRANSFORMING LINE/ARROW');
  console.log('[entityWriteCore] Input content:', JSON.stringify(content, null, 2));
  console.log('[entityWriteCore] className:', content.className);
  console.log('[entityWriteCore] isSlideShape:', isSlideShapeLineOrArrow);
  console.log('[entityWriteCore] attrs.start:', JSON.stringify(content.attrs.start));
  console.log('[entityWriteCore] attrs.end:', JSON.stringify(content.attrs.end));
  
  // Resolve start point (absolute or anchored)
  const startPoint = await resolvePoint(content.attrs.start, app, 'start');
  const endPoint = await resolvePoint(content.attrs.end, app, 'end');
  
  console.log('[entityWriteCore] ✅ Resolved points:');
  console.log('  startPoint:', startPoint);
  console.log('  endPoint:', endPoint);
  
  // Calculate bounding box
  const minX = Math.min(startPoint.x, endPoint.x);
  const maxX = Math.max(startPoint.x, endPoint.x);
  const minY = Math.min(startPoint.y, endPoint.y);
  const maxY = Math.max(startPoint.y, endPoint.y);
  
  console.log('[entityWriteCore] 📦 Bounding box calculation:');
  console.log('  minX = min(', startPoint.x, ',', endPoint.x, ') =', minX);
  console.log('  maxX = max(', startPoint.x, ',', endPoint.x, ') =', maxX);
  console.log('  minY = min(', startPoint.y, ',', endPoint.y, ') =', minY);
  console.log('  maxY = max(', startPoint.y, ',', endPoint.y, ') =', maxY);
  
  // Ensure minimum size (1px) for zero-length lines
  const width = Math.max(maxX - minX, 1);
  const height = Math.max(maxY - minY, 1);
  
  console.log('[entityWriteCore] 📏 Dimensions:');
  console.log('  width = max(', maxX - minX, ', 1) =', width);
  console.log('  height = max(', maxY - minY, ', 1) =', height);
  
  // Convert absolute coordinates to relative (within bounding box)
  const relativeX1 = startPoint.x - minX;
  const relativeY1 = startPoint.y - minY;
  const relativeX2 = endPoint.x - minX;
  const relativeY2 = endPoint.y - minY;
  
  console.log('[entityWriteCore] 🔄 Converting to relative coordinates:');
  console.log('  relativeX1 =', startPoint.x, '-', minX, '=', relativeX1);
  console.log('  relativeY1 =', startPoint.y, '-', minY, '=', relativeY1);
  console.log('  relativeX2 =', endPoint.x, '-', minX, '=', relativeX2);
  console.log('  relativeY2 =', endPoint.y, '-', minY, '=', relativeY2);
  
  console.log('[entityWriteCore] ✅ FINAL OUTPUT:');
  console.log('  position:', { x: minX, y: minY, width, height });
  console.log('  semantic:', { x1: relativeX1, y1: relativeY1, x2: relativeX2, y2: relativeY2 });
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  
  // Return content with position+semantic format (internal storage format)
  // ✅ NOTE: For slide-shape, this preserves semantic.shapeType from line 714
  const { start, end, ...otherAttrs } = content.attrs;
  
  return {
    ...content,
    attrs: {
      ...otherAttrs,
      position: {
        x: minX,
        y: minY,
        width,
        height
      },
      semantic: {
        ...content.attrs.semantic,  // ✅ Preserves shapeType for slide-shape
        x1: relativeX1,
        y1: relativeY1,
        x2: relativeX2,
        y2: relativeY2
      }
    }
  };
}

/**
 * Resolve a point (for line/arrow endpoint) to absolute coordinates
 * Point can be: { x, y } OR { anchorTo, anchorPoint, offset }
 */
async function resolvePoint(
  point: any,
  app: any,
  pointName: string
): Promise<{ x: number; y: number }> {
  console.log('[resolvePoint] 🎯 Resolving point:', pointName, JSON.stringify(point));
  
  // If absolute coordinates
  if (typeof point.x === 'number' && typeof point.y === 'number') {
    console.log('[resolvePoint] ✅ Using explicit coordinates:', { x: point.x, y: point.y });
    return { x: point.x, y: point.y };
  }
  
  // If anchored
  if (point.anchorTo && point.anchorPoint) {
    console.log('[resolvePoint] 🔗 Using anchor:', { anchorTo: point.anchorTo, anchorPoint: point.anchorPoint });
    
    // Ensure indices are built
    if (typeof app.buildCoreIndices === 'function') {
      await app.buildCoreIndices();
    }
    
    // Lookup anchor node
    const anchorNode = app.byIdIndex?.get(point.anchorTo);
    
    if (!anchorNode) {
      console.error('[resolvePoint] ❌ Anchor node not found:', point.anchorTo);
      throw new Error(`Anchor node not found for ${pointName}: ${point.anchorTo}. Use entity_get_metadata to find valid node IDs.`);
    }
    
    console.log('[resolvePoint] Found anchor node:', {
      id: anchorNode.id,
      className: anchorNode.className,
      position: anchorNode.attrs?.position
    });
    
    // Check for chained anchors (not allowed)
    if (anchorNode.attrs?.position?.anchorTo) {
      console.error('[resolvePoint] ❌ Cannot anchor to anchored element:', point.anchorTo);
      throw new Error(`Cannot anchor to an anchored element (${point.anchorTo}). Anchor target must have absolute position.`);
    }
    
    // Get anchor point coordinates (CENTER of edges!)
    const anchorPoint = point.anchorPoint as AnchorPoint;
    const anchorCoords = getAnchorPointCoordinates(anchorNode, anchorPoint);
    
    console.log('[resolvePoint] Calculated anchor coords:', anchorCoords);
    
    // Apply user offset
    const offset = point.offset || { x: 0, y: 0 };
    
    const resolvedX = anchorCoords.x + offset.x;
    const resolvedY = anchorCoords.y + offset.y;
    
    console.log('[resolvePoint] Applied offset:', offset, '→ Final:', { x: resolvedX, y: resolvedY });
    
    // Validate bounds for Canvas
    const bounds = getBoundsForEntity(app);
    if (resolvedX < bounds.minX || resolvedX > bounds.maxX ||
        resolvedY < bounds.minY || resolvedY > bounds.maxY) {
      throw new Error(
        `Resolved ${pointName} is outside valid bounds. ` +
        `Position: (${resolvedX.toFixed(1)}, ${resolvedY.toFixed(1)}), ` +
        `Valid bounds: x(${bounds.minX} to ${bounds.maxX}), y(${bounds.minY} to ${bounds.maxY}). ` +
        `Anchor: ${point.anchorTo} at ${anchorPoint}, Offset: (${offset.x}, ${offset.y})`
      );
    }
    
    return {
      x: resolvedX,
      y: resolvedY
    };
  }
  
  throw new Error(`${pointName} must have either {x, y} coordinates OR {anchorTo, anchorPoint}`);
}
function transformSheetContent(params: BaseWriteParams): BaseWriteParams {
  const { content, attrs, operation, operations } = params;
  
  // Handle batch operations
  if (operation === 'batch' && operations) {
    const transformedOperations = operations.map(op => {
      if (op.content) {
        // Recursively transform this operation's content
        const transformed = transformSheetContent({ 
          entityId: params.entityId,  // Required field
          content: op.content, 
          attrs: op.attrs,
          operation: op.op || op.operation,
          operations: undefined
        });
        
        // Return operation with transformed attrs and no content
        return {
          ...op,
          attrs: transformed.attrs,
          content: undefined
        };
      }
      return op;
    });
    
    return {
      ...params,
      operations: transformedOperations
    };
  }
  
  // If no content, return as-is
  if (!content) {
    return params;
  }
  
  console.log('[entityWriteCore] Transforming sheet content format');
  
  // ✅ DEBUG: Log content structure to diagnose transformation issues
  console.log('[entityWriteCore] 🔍 Content structure:', {
    hasContent: !!content,
    isArray: Array.isArray(content),
    length: Array.isArray(content) ? content.length : 1,
    firstCell: Array.isArray(content) && content[0] ? {
      keys: Object.keys(content[0]),
      className: content[0].className,
      hasAttrs: !!content[0].attrs,
      attrsKeys: content[0].attrs ? Object.keys(content[0].attrs) : []
    } : (content ? {
      keys: Object.keys(content),
      className: content.className,
      hasAttrs: !!content.attrs
    } : null)
  });
  
  // Initialize attrs map
  const transformedAttrs: Record<string, any> = { ...attrs };
  
  // Get cells array
  const cells = Array.isArray(content) ? content : [content];
  
  // Transform each cell
  for (const cell of cells) {
    // ✅ DEBUG: Log each cell being processed
    console.log('[entityWriteCore] 🔍 Processing cell:', {
      hasClassName: !!cell.className,
      className: cell.className,
      cellKeys: Object.keys(cell),
      hasAttrs: !!cell.attrs
    });
    
    // ✅ BUG FIX: More lenient className check
    // Accept cells with className="table_cell" OR cells with cellRef (backward compat)
    if (cell.className && cell.className !== 'table_cell') {
      console.warn('[entityWriteCore] ⚠️ SKIPPING cell - unexpected className:', {
        expected: 'table_cell',
        actual: cell.className,
        cellKeys: Object.keys(cell)
      });
      continue;
    }
    
    // Extract cellRef (check both locations)
    const cellRef = cell.attrs?.position?.cellRef || cell.attrs?.semantic?.cellRef;
    if (!cellRef) {
      console.warn('[entityWriteCore] ⚠️ SKIPPING cell - missing cellRef:', {
        hasClassName: !!cell.className,
        className: cell.className,
        hasAttrs: !!cell.attrs,
        attrsKeys: cell.attrs ? Object.keys(cell.attrs) : [],
        cellStructure: Object.keys(cell)
      });
      continue;
    }
    
    // Extract value (check both locations, null is valid)
    const value = cell.attrs?.semantic?.value ?? cell.attrs?.value;
    
    console.log('[entityWriteCore] ✅ Transformed cell:', { cellRef, value: String(value).substring(0, 50) });
    
    // Store in attrs map
    transformedAttrs[cellRef] = value;
  }
  
  console.log('[entityWriteCore] Transformed cells:', Object.keys(transformedAttrs).length);
  
  // Return params with attrs instead of content
  return {
    ...params,
    attrs: transformedAttrs,
    content: undefined  // Remove content (now in attrs)
  };
}

/**
 * Pre-fetch images for all content items (handles single items, arrays, and batch operations)
 */
async function preFetchImages(params: BaseWriteParams): Promise<void> {
  const { content, operations } = params;
  
  // Handle batch operations
  if (operations && Array.isArray(operations)) {
    for (const op of operations) {
      if (op.content) {
        await preFetchImagesInContent(op.content);
      }
    }
    return;
  }
  
  // Handle single content item
  if (content) {
    // Handle array of content items
    if (Array.isArray(content)) {
      for (const item of content) {
        await preFetchImagesInContent(item);
      }
    } else {
      // Handle single content object
      await preFetchImagesInContent(content);
    }
  }
}

/**
 * HANDLER CORE - Common execution pattern for all write tools
 * 
 * All tools use this same flow:
 * 1. Get application instance
 * 2. Pre-fetch images (if any imageSource fields present)
 * 3. Execute write via BaseApplication (handles Y.js, transactions, change tracking)
 * 4. Extract content for display
 * 5. Generate contextual message
 * 6. Return formatted result
 */
export async function executeWrite(
  params: BaseWriteParams,
  entityPrefix: string,
  context: CommandExecutionContext
): Promise<StandardToolResult> {
  const { entityId, operation } = params;
  
  console.log(`[${entityPrefix}_write] Operation:`, operation);
  console.log(`[${entityPrefix}_write] Entity:`, entityId);
  
  try {
    // 1. Get application instance
    const app = await getApplicationForEntity(entityId);
    
    if (!app) {
      return {
        status: 'error',
        message: 'Entity not found or not loaded',
        error: { 
          code: 'entity_not_found', 
          category: 'execution', 
          details: `${entityPrefix} entity not found in application registry` 
        },
        format: 'plaintext'
      };
    }
    
    // Transform content format for sheets
    let transformedParams = params;
    if (entityPrefix === 'sheet') {
      transformedParams = transformSheetContent(params);
    }
    
    // Resolve anchored positions (canvas, presentations)
    if (entityPrefix === 'canvas' || entityPrefix === 'slide') {
      transformedParams = await resolveAnchors(transformedParams, app);
    }
    
    // 2. Pre-fetch images (BEFORE Y.js transaction)
    // This converts imageSource + prompt/query to actual image URLs
    await preFetchImages(transformedParams);
    
    // 3. Execute write via BaseApplication
    // This handles Y.js transactions, change tracking, and index updates
    const result = await app.handleEntityWrite({
      operation: transformedParams.operation,
      target: transformedParams.target,
      content: transformedParams.content,
      attrs: transformedParams.attrs,
      operations: transformedParams.operations
    });

    console.log(`[${entityPrefix}_write] ✅ Write complete:`, {
      entityId,
      operation: transformedParams.operation,
      affectedNodes: result.affectedNodes?.length ?? 0,
    });
    
    // 4. Extract content for UI display
    const insertedContent = extractContentFromParams(transformedParams.content, transformedParams.attrs, transformedParams.operation, transformedParams.operations);
    
    // 5. Generate user-facing message
    const entityTypeName = entityPrefix.replace('_', '');
    const userMessage = generateContextualMessage(transformedParams.operation, entityTypeName, insertedContent);
    
    // 6. Return formatted result
    return {
      status: 'success',
      message: userMessage,
      data: {
        entityId,
        operation: transformedParams.operation,
        affectedNodes: result.affectedNodes || [],
        stats: {
          nodesModified: result.affectedNodes?.length || 0
        },
        messageContext: {
          fullContent: insertedContent,
          contentPreview: insertedContent && insertedContent.length > 150 
            ? insertedContent.substring(0, 150) + '...' 
            : insertedContent,
          operationSummary: `${transformedParams.operation.charAt(0).toUpperCase()}${transformedParams.operation.slice(1)}ed content`
        }
      },
      format: 'json'
    };
    
  } catch (error: any) {
    console.error(`[${entityPrefix}_write] Error:`, error);
    
    return {
      status: 'error',
      message: `Failed to write to ${entityPrefix.replace('_', '')}`,
      error: error.message,
      format: 'plaintext'
    };
  }
}
